import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../axiosInstance";
import { shouldToastFailure } from "../../components/Dashboard/ActionRequired/actionRequiredView";

/**
 * GET /api/system/monitoring — the combined monitoring view (Master Ch 1 §12A, UI-26).
 *
 * Read-only. The endpoint fetches each part on its own with a short timeout and reports a part
 * it could not read as `{ error }` rather than failing the whole response, so a stopped search
 * cache still returns a page. This hook keeps that shape intact: it never flattens an errored
 * part away, because a panel that silently shows nothing is indistinguishable from one that is
 * fine, which is the opposite of what a monitoring page is for.
 *
 * The same router also serves the incident history (GET/PATCH /system/incidents) and the
 * dashboard's "Action required" list (GET /system/action-required); their calls live here too.
 */
const BASE = "/system/monitoring";
const INCIDENTS = "/system/incidents";
const ACTION_REQUIRED = "/system/action-required";

/** The incident list is paged by the backend (limit 50 by default, 200 at most). */
export const INCIDENT_PAGE_SIZE = 50;

/**
 * One readable message for a failed call. A response without a message names its HTTP status;
 * no response at all means the admin API could not be reached.
 */
export const apiErrorMessage = (err, fallback) => {
  if (err?.response) {
    return err.response.data?.message || (err.response.status ? `${fallback} (HTTP ${err.response.status})` : fallback);
  }
  if (err?.request) return "Could not reach the admin API.";
  return err?.message || fallback;
};

export const getMonitoring = () => axiosInstance.get(BASE).then((res) => res.data?.data ?? null);

/** GET /system/incidents?status=active|resolved|all&limit&offset, newest first. */
export const getIncidents = ({ status = "active", limit = INCIDENT_PAGE_SIZE, offset = 0 } = {}) =>
  axiosInstance.get(INCIDENTS, { params: { status, limit, offset } }).then((res) => res.data?.data ?? null);

/** PATCH /system/incidents/:id { status, note? }: a person takes or closes an incident. */
export const updateIncident = (id, body) =>
  axiosInstance.patch(`${INCIDENTS}/${encodeURIComponent(id)}`, body).then((res) => res.data?.data ?? null);

/** GET /system/action-required: unresolved incidents and paid-but-unconfirmed online bookings. */
export const getActionRequired = () => axiosInstance.get(ACTION_REQUIRED).then((res) => res.data?.data ?? null);

export const useMonitoring = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getMonitoring());
      return { ok: true };
    } catch (err) {
      // The whole request failed, which is different from a part failing. Keep whatever was
      // last shown rather than blanking the page: stale numbers with a visible error beat an
      // empty screen when you are trying to work out what is broken.
      const message = apiErrorMessage(err, "Could not load the monitoring view.");
      setError(message);
      return { ok: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { data, loading, error, refresh };
};

/**
 * The incident history for the Monitoring page: one tab (active | resolved | all) and one page
 * of it at a time.
 *
 * `data` carries the status and offset it was loaded for, so the page can tell a list that
 * belongs to the tab on screen from one still left over from the previous tab. A slower answer
 * for a tab the user already left is dropped instead of overwriting the newer one.
 */
export const useIncidents = (initialStatus = "active") => {
  const [status, setStatusState] = useState(initialStatus);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latest = useRef(0);

  const load = useCallback(async (s, o) => {
    const mine = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const d = await getIncidents({ status: s, offset: o });
      if (mine === latest.current) {
        setData({ total: 0, items: [], ...(d || {}), status: s, offset: o });
      }
      return { ok: true };
    } catch (err) {
      const message = apiErrorMessage(err, "Could not load the incidents.");
      if (mine === latest.current) setError(message);
      return { ok: false, message };
    } finally {
      if (mine === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(status, offset); }, [load, status, offset]);

  const refresh = useCallback(() => load(status, offset), [load, status, offset]);
  const setStatus = useCallback((next) => {
    setStatusState(next);
    setOffset(0);
  }, []);

  return { status, setStatus, offset, setOffset, data, loading, error, refresh, pageSize: INCIDENT_PAGE_SIZE };
};

/**
 * The dashboard's "Action required" list, polled so a dashboard left open does not keep showing
 * an old answer. A failed poll keeps the last list but records the error, which is what stops
 * the section from claiming "Everything is running" on data it could not refresh.
 *
 * `onError(message)` is called for a failure the user should hear about: every failed manual
 * refresh, and the first failed poll of an outage (not one toast a minute while it lasts).
 */
export const useActionRequired = ({ pollMs = 60000, onError } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const failing = useRef(false);
  const onErrorRef = useRef(onError);

  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const refresh = useCallback(async ({ manual = false } = {}) => {
    try {
      const d = await getActionRequired();
      setData(d);
      setError(null);
      failing.current = false;
      return { ok: true };
    } catch (err) {
      const message = apiErrorMessage(err, "Could not load what needs action.");
      setError(message);
      if (shouldToastFailure({ manual, wasFailing: failing.current })) onErrorRef.current?.(message);
      failing.current = true;
      return { ok: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    if (!pollMs) return undefined;
    const t = setInterval(() => { refresh(); }, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  return { data, loading, error, refresh };
};

export default useMonitoring;
